import { useState } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import { useForm, useFieldArray } from "react-hook-form";
import moment from 'moment';

// Custom Component
import TournamentForm from '../Form/TournamentForm';
import { PrimaryButton } from '../Button';
import SuccessAlert from '../Alert/SuccessAlert';
import ErrorAlert from '../Alert/ErrorAlert';
import ConfirmAlert from "../Alert/ConfirmAlert";

// Images
import { Image } from '../../assets/images/svg';

// Utils
import { isMixedEvent, isAgeValueRequired, getGenderCount, getPlayerCount, convertEventType } from '../../utils/helper';

// API Service
import { addTournamentService } from '../../service/tournament.service';

const CreateTournamentModal = (props) => {
    const [tournamentImage, setTournamentImage] = useState(null);
    const [loading, setLoading] = useState(false);
    LoadingOverlay.propTypes = undefined

    const { register, control, watch, setValue, reset, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {
            sports_type: "BADMINTON",
            category_type: ["INDIVIDUAL"],
            individual_events: [
                {
                    event_type: "MEN_SINGLE",
                    age_groups: [
                        {
                            age_group: "BELOW_AGE",
                            age_value: "",
                            price: "",
                            number_of_males: "",
                            number_of_females: "",
                            allowed_registrations: "",
                            is_disabled: true,
                            allow_proffesional_players: false
                        }
                    ]
                }
            ]
        },
        mode: "all"
    })

    const { fields: individualEventsFields, append: individualEventsAppend, remove: individualEventsRemove } = useFieldArray({
        control,
        name: "individual_events"
    });

    const { fields: teamEventsFields, append: teamEventsAppend } = useFieldArray({
        control,
        name: "team_events"
    });

    /**
        * @function onSubmit
        * @params formdata
        * @description used to create tournament
    */
    const onSubmit = async (formdata) => {
        try {
            if (!tournamentImage) {
                ErrorAlert('You need to upload a tournament image')
                return
            }
            const data = new FormData()
            if (formdata?.organization_id?.length > 0) {
                formdata?.organization_id?.forEach((item, index) => {
                    data.append(`organization_ids[${index}]`, item?.value)
                })
            }
            data.append('name', formdata?.name)
            data.append('description', formdata?.description)
            data.append('prizes', formdata?.prizes)
            data.append('terms_and_condition', formdata?.terms_and_condition)
            data.append('banner_image', tournamentImage)
            data.append('sports_type', formdata?.sports_type)
            data.append('category_type', "INDIVIDUAL")
            data.append('registration_start_date', moment(formdata?.registration_start_date).format("YYYY-MM-DD"))
            data.append('registration_end_date', moment(formdata?.registration_end_date).format("YYYY-MM-DD"))
            data.append('tournament_start_date', moment(formdata?.tournament_start_date).format("YYYY-MM-DD"))
            data.append('tournament_end_date', moment(formdata?.tournament_end_date).format("YYYY-MM-DD"))
            data.append('tournament_start_time', moment(formdata?.tournament_start_time, "hh:mm A").format("HH:mm"))
            data.append('tournament_end_time', moment(formdata?.tournament_end_time, "hh:mm A").format("HH:mm"))
            if (formdata?.category_type?.includes("INDIVIDUAL") && formdata?.individual_events?.length > 0) {
                formdata?.individual_events?.forEach((item, index) => {
                    if (item?.event_type) {
                        data.append(`individual_events[${index}][event_type]`, item?.event_type)
                    }
                    if (item?.age_groups?.length > 0) {
                        item?.age_groups?.forEach((ageGroupsItem, ageGroupsIndex) => {
                            if (ageGroupsItem?.age_group) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][age_group]`, ageGroupsItem?.age_group)
                            }
                            if (isAgeValueRequired(ageGroupsItem?.age_group) && ageGroupsItem?.age_value) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][age_value]`, ageGroupsItem?.age_value)
                            }
                            if (ageGroupsItem?.price) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][price]`, ageGroupsItem?.price)
                            }
                            if (item?.event_type !== "GROUP_CAPTAIN" && item?.event_type !== "JUNIOR_SINGLE") {
                                const genderCount = getGenderCount(item?.event_type);
                                if (genderCount) {
                                    data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_males]`, genderCount?.number_of_males)
                                    data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_females]`, genderCount?.number_of_females)
                                }
                            }
                            if (ageGroupsItem?.number_of_males && ageGroupsItem?.number_of_females && isMixedEvent(item?.event_type) && (parseInt(ageGroupsItem?.number_of_males) + parseInt(ageGroupsItem?.number_of_females)) !== getPlayerCount(item?.event_type)) {
                                ErrorAlert(`You need to choose ${getPlayerCount(item?.event_type)} person for ${convertEventType(item?.event_type)} event`)
                                return
                            }
                            if (ageGroupsItem?.number_of_males && isMixedEvent(item?.event_type)) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_males]`, ageGroupsItem?.number_of_males)
                            }
                            if (ageGroupsItem?.number_of_females && isMixedEvent(item?.event_type)) {
                                data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][number_of_females]`, ageGroupsItem?.number_of_females)
                            }
                            data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][allowed_registrations]`, ageGroupsItem?.allowed_registrations)
                            data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][is_disabled]`, ageGroupsItem?.is_disabled ? 0 : 1)
                            data.append(`individual_events[${index}][age_groups][${ageGroupsIndex}][allow_proffesional_players]`, ageGroupsItem?.allow_proffesional_players ? 1 : 0)
                        })
                    }
                })
            }
            if (formdata?.category_type?.includes("TEAM") && formdata?.team_events?.length > 0) {
                formdata?.team_events?.forEach((item, index) => {
                    if (item?.event_name) {
                        data.append(`team_events[${index}][event_name]`, item?.event_name)
                    }
                    if (item?.number_of_participants) {
                        data.append(`team_events[${index}][number_of_participants]`, item?.number_of_participants)
                    }
                    if (item?.price) {
                        data.append(`team_events[${index}][price]`, item?.price)
                    }
                    if (item?.event_details?.length > 0) {
                        item?.event_details?.forEach((eventDetailsItem, eventDetailsIndex) => {
                            if (eventDetailsItem?.event_type) {
                                data.append(`team_events[${index}][event_details][${eventDetailsIndex}][event_type]`, eventDetailsItem?.event_type)
                            }
                            if (eventDetailsItem?.age_groups?.length > 0) {
                                eventDetailsItem?.age_groups?.forEach((ageGroupsItem, ageGroupsIndex) => {
                                    if (ageGroupsItem?.age_group) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][age_group]`, ageGroupsItem?.age_group)
                                    }
                                    if (isAgeValueRequired(ageGroupsItem?.age_group) && ageGroupsItem?.age_value) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][age_value]`, ageGroupsItem?.age_value)
                                    }
                                    if (eventDetailsItem?.event_type !== "GROUP_CAPTAIN" && eventDetailsItem?.event_type !== "JUNIOR_SINGLE") {
                                        const genderCount = getGenderCount(eventDetailsItem?.event_type);
                                        if (genderCount) {
                                            data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_males]`, genderCount?.number_of_males)
                                            data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_females]`, genderCount?.number_of_females)
                                        }
                                    }
                                    if (ageGroupsItem?.number_of_males && ageGroupsItem?.number_of_females && isMixedEvent(eventDetailsItem?.event_type) && (parseInt(ageGroupsItem?.number_of_males) + parseInt(ageGroupsItem?.number_of_females)) !== getPlayerCount(eventDetailsItem?.event_type)) {
                                        ErrorAlert(`You need to choose ${getPlayerCount(eventDetailsItem?.event_type)} person for ${convertEventType(eventDetailsItem?.event_type)} event`)
                                        return
                                    }
                                    if (ageGroupsItem?.number_of_males && isMixedEvent(eventDetailsItem?.event_type)) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_males]`, ageGroupsItem?.number_of_males)
                                    }
                                    if (ageGroupsItem?.number_of_females && isMixedEvent(eventDetailsItem?.event_type)) {
                                        data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][number_of_females]`, ageGroupsItem?.number_of_females)
                                    }
                                    data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][is_disabled]`, ageGroupsItem?.is_disabled ? 0 : 1)
                                    data.append(`team_events[${index}][event_details][${eventDetailsIndex}][age_groups][${ageGroupsIndex}][allow_proffesional_players]`, ageGroupsItem?.allow_proffesional_players ? 1 : 0)
                                })
                            }
                        })
                    }
                })
            }
            data.append('contact', formdata?.contact)
            data.append('phone_number', formdata?.phone_number)
            data.append('address', formdata?.address)
            if (formdata?.category_type?.includes("TEAM")) {
                data.append('number_of_team_event', parseInt(formdata?.number_of_team_event))
            }
            setLoading(true)
            const result = await addTournamentService(data);
            if (result?.data?.status) {
                SuccessAlert(result?.data?.message)
                props?.fetchTournamentList(1)
                handleClose()
            } else {
                ErrorAlert(result?.response?.data?.message)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            ErrorAlert(error)
        }
    }

    /**
        * @function handleClose
        * @params
        * @description used to close create tournament modal
    */
    const handleClose = async () => {
        reset({
            sports_type: "BADMINTON",
            category_type: ["INDIVIDUAL"],
            individual_events: [
                {
                    event_type: "MEN_SINGLE",
                    age_groups: [
                        {
                            age_group: "BELOW_AGE",
                            age_value: "",
                            price: "",
                            number_of_males: "",
                            number_of_females: "",
                            allowed_registrations: "",
                            is_disabled: true,
                            allow_proffesional_players: false
                        }
                    ]
                }
            ],
            team_events: []
        })
        setTournamentImage()
        props.handleClose()
    }

    return <Modal show={props.show} onHide={() => ConfirmAlert('Are you sure on closing this?', "You will loose all progress.", handleClose)} size={props.size} centered backdrop="static" keyboard={false}>
        <LoadingOverlay
            active={loading}
            spinner={<Spinner animation="border" />}
        >
            <Modal.Header className='fs-22 fw-bold border-0' closeButton >
                Create Tournament
            </Modal.Header>
            <Modal.Body className='d-flex flex-column align-items-center'>
                <Form className='w-100' onSubmit={handleSubmit(onSubmit)}>
                    <input className='d-none' type='file' id="tournamentPhotoUpload" accept="image/*" onChange={(e) => {
                        if (e.target.files.length > 0) {
                            const file = e.target.files[0];
                            const img = document.createElement("img");
                            img.onload = function () {
                                if (img.width <= 960 && (img.width / img.height)?.toFixed(2) === (23 / 9)?.toFixed(2)) {
                                    setTournamentImage(file)
                                } else {
                                    ErrorAlert("Please upload the correct ratio tournament image. Ratio will be 23/9")
                                }
                            };
                            img.src = URL.createObjectURL(file);
                        }
                    }} onClick={(e) => e.target.value = null} />
                    <label className='w-100' htmlFor="tournamentPhotoUpload">
                        {tournamentImage
                            ?
                            <img role='button' className='w-100 img-fluid' src={URL.createObjectURL(tournamentImage)} alt={'tournamentImage'} />
                            :
                            <div role='button' className='d-flex flex-column justify-content-center align-items-center bg-danger bg-opacity-10 py-5'>
                                <Image className='mb-3' />
                                <span className='fs-18 fw-600 text-primary mb-3'>Add Banners here</span>
                                <span className='fw-400 text-secondary'>Maximum width 960 pixels, 23:9 recommended</span>
                            </div>
                        }
                    </label>

                    <TournamentForm
                        {...{ register, control, watch, setValue, errors, individualEventsFields, individualEventsAppend, individualEventsRemove, teamEventsFields, teamEventsAppend }}
                    />

                    <div className='d-flex justify-content-end align-items-end mt-4'>
                        <div>
                            <PrimaryButton className='fs-14 fw-500 px-5' disabled={loading} loading={loading} type="submit">Create</PrimaryButton>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
        </LoadingOverlay>
    </Modal>
};

export default CreateTournamentModal;